import React, { useState, useEffect } from 'react';
import { Plus, Calendar, User, AlertCircle, Clock, CheckCircle, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { ocorrenciaService, eventoService, ocorrenciaTipoService } from '../services/api';

const OcorrenciasKanban = ({ eventoId = null }) => {
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const { data: tipos } = useCrud(ocorrenciaTipoService, 'tipos de ocorrências');
  const modal = useModal();
  const [selectedEvento, setSelectedEvento] = useState(eventoId || '');
  const [kanbanData, setKanbanData] = useState({
    ABERTO: [],
    EM_ANDAMENTO: [],
    FECHADO: []
  });
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  // Removido expandedLanes - layout horizontal

  const validationRules = {
    evento_id: { required: true, label: 'Evento' },
    data_abertura: { required: true, label: 'Data de Abertura' },
    status: { required: false, label: 'Status' },
    descricao: { required: false, label: 'Descrição' },
    nome_solicitante: { required: false, label: 'Nome do Solicitante' },
    tipo_id: { required: false, label: 'Tipo' },
    observacoes: { required: false, label: 'Observações' }
  };

  const form = useForm(
    {
      evento_id: '',
      data_abertura: new Date().toISOString().split('T')[0],
      status: 'ABERTO',
      descricao: '',
      nome_solicitante: '',
      tipo_id: '',
      observacoes: ''
    },
    validationRules
  );

  // Carregar dados do Kanban quando evento é selecionado
  useEffect(() => {
    if (selectedEvento) {
      loadKanbanData();
    }
  }, [selectedEvento]);

  const loadKanbanData = async () => {
    setLoading(true);
    try {
      const response = await ocorrenciaService.getKanban(selectedEvento);
      setKanbanData(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar dados do Kanban:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventoChange = (eventoId) => {
    setSelectedEvento(eventoId);
    form.setValue('evento_id', eventoId);
  };

  const handleCreate = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    form.reset({
      evento_id: selectedEvento,
      data_abertura: today,
      status: 'ABERTO',
      descricao: '',
      nome_solicitante: '',
      tipo_id: '',
      observacoes: ''
    });
    modal.open({ mode: 'create', title: 'Nova Ocorrência' });
  };

  const handleEdit = (ocorrencia) => {
    form.reset({
      evento_id: ocorrencia.evento_id || '',
      data_abertura: ocorrencia.data_abertura ? ocorrencia.data_abertura.split('T')[0] : new Date().toISOString().split('T')[0],
      status: ocorrencia.status || 'ABERTO',
      descricao: ocorrencia.descricao || '',
      nome_solicitante: ocorrencia.nome_solicitante || '',
      tipo_id: ocorrencia.tipo_id || '',
      observacoes: ocorrencia.observacoes || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Ocorrência', id: ocorrencia.id });
  };

  const handleSubmit = async (values) => {
    try {
      const cleanedValues = { ...values };
      
      // Na criação, garantir que o status seja sempre ABERTO
      if (modal.data.mode === 'create') {
        cleanedValues.status = 'ABERTO';
        cleanedValues.evento_id = selectedEvento;
      }
      
      if (!cleanedValues.evento_id || cleanedValues.evento_id === '') {
        cleanedValues.evento_id = null;
      }
      if (!cleanedValues.tipo_id || cleanedValues.tipo_id === '') {
        cleanedValues.tipo_id = null;
      }

      const result = modal.data.mode === 'create' 
        ? await ocorrenciaService.create(cleanedValues)
        : await ocorrenciaService.update(modal.data.id, cleanedValues);

      if (result.data.success) {
        modal.close();
        form.reset();
        loadKanbanData(); // Recarregar dados
      }
    } catch (error) {
      console.error('Erro ao salvar ocorrência:', error);
    }
  };

  // Drag & Drop functions
  const handleDragStart = (e, ocorrencia) => {
    setDraggedItem(ocorrencia);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    
    if (draggedItem && draggedItem.status !== newStatus) {
      try {
        await ocorrenciaService.updateStatus(draggedItem.id, { status: newStatus });
        loadKanbanData(); // Recarregar dados
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
      }
    }
    
    setDraggedItem(null);
  };

  // Funções removidas - layout horizontal sem toggle

  const getStatusConfig = (status) => {
    const configs = {
      ABERTO: {
        title: 'SOLICITADO',
        subtitle: 'REQUESTED',
        icon: AlertCircle,
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        textColor: 'text-white',
        cardBorder: 'border-l-4 border-l-blue-500',
        wipLimit: null
      },
      EM_ANDAMENTO: {
        title: 'EM PROGRESSO',
        subtitle: 'IN PROGRESS',
        icon: Clock,
        color: 'bg-orange-500',
        bgColor: 'bg-orange-50',
        textColor: 'text-white',
        cardBorder: 'border-l-4 border-l-orange-500',
        wipLimit: 3
      },
      FECHADO: {
        title: 'CONCLUÍDO',
        subtitle: 'DONE',
        icon: CheckCircle,
        color: 'bg-green-500',
        bgColor: 'bg-green-50',
        textColor: 'text-white',
        cardBorder: 'border-l-4 border-l-green-500',
        wipLimit: null
      }
    };
    return configs[status] || configs.ABERTO;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="p-6">
      {/* Header com seletor de evento */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Ocorrências - Kanban</h1>
        <div className="flex gap-4 items-center">
          {!eventoId && (
            <select
              value={selectedEvento}
              onChange={(e) => handleEventoChange(e.target.value)}
              className="form-select"
            >
              <option value="">Selecione um evento</option>
              {eventos.map((evento) => (
                <option key={evento.id} value={evento.id}>
                  {evento.nome}
                </option>
              ))}
            </select>
          )}
          {selectedEvento && (
            <button onClick={handleCreate} className="btn btn-primary">
              <Plus size={20} />
              Nova Ocorrência
            </button>
          )}
        </div>
      </div>

      {/* Kanban Board - Layout Sanfona */}
      {selectedEvento ? (
        loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : (
          <div className="kanban-horizontal-board">
            {Object.keys(kanbanData).map((status) => {
              const config = getStatusConfig(status);
              const Icon = config.icon;
              const currentCount = kanbanData[status].length;
              const isOverWipLimit = config.wipLimit && currentCount > config.wipLimit;
              
              return (
                <div
                  key={status}
                  className={`kanban-column ${config.bgColor} border border-gray-200 rounded-lg shadow-sm`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, status)}
                >
                  {/* Header da coluna */}
                  <div className={`${config.color} ${config.textColor} p-3 text-center font-bold text-sm`}>
                    <div className="mb-1">{config.title}</div>
                    <div className="text-xs opacity-80 font-normal">{config.subtitle}</div>
                    {config.wipLimit && (
                      <div className={`text-xs mt-1 px-2 py-1 rounded ${
                        isOverWipLimit 
                          ? 'bg-red-500 text-white' 
                          : 'bg-white bg-opacity-20'
                      }`}>
                        {currentCount}/{config.wipLimit}
                      </div>
                    )}
                    {!config.wipLimit && (
                      <div className="text-xs mt-1 px-2 py-1 rounded bg-white bg-opacity-20">
                        {currentCount}
                      </div>
                    )}
                  </div>

                  {/* Cards da coluna */}
                  <div className="p-3 space-y-3 min-h-96">
                    {kanbanData[status].length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Icon size={32} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Arraste cards aqui</p>
                      </div>
                    ) : (
                      kanbanData[status].map((ocorrencia) => (
                        <div
                          key={ocorrencia.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, ocorrencia)}
                          className={`ocorrencia-card bg-white rounded-lg shadow-sm border cursor-move hover:shadow-lg transition-all duration-200 transform hover:-translate-y-1 overflow-hidden ${config.cardBorder}`}
                          onClick={() => handleEdit(ocorrencia)}
                        >
                          {/* Conteúdo do Card Compacto */}
                          <div className="p-3">
                            {/* Data no topo */}
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                              <Calendar size={12} />
                              <span>{formatDate(ocorrencia.data_abertura)}</span>
                              {ocorrencia.data_fechamento && (
                                <>
                                  <span className="mx-1">→</span>
                                  <CheckCircle size={12} />
                                  <span>{formatDate(ocorrencia.data_fechamento)}</span>
                                </>
                              )}
                            </div>
                            
                            {/* Descrição Principal */}
                            <h4 className="font-medium text-gray-900 text-sm line-clamp-2 leading-tight mb-2">
                              {ocorrencia.descricao || 'Sem descrição'}
                            </h4>
                            
                            {/* Solicitante */}
                            {ocorrencia.nome_solicitante && (
                              <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                                <User size={12} className="text-gray-400" />
                                <span className="truncate">{ocorrencia.nome_solicitante}</span>
                              </div>
                            )}
                            
                            {/* Tipo como badge pequeno */}
                            {ocorrencia.OcorrenciaTipo && (
                              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {ocorrencia.OcorrenciaTipo.nome}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="text-center py-12 text-gray-500">
          <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
          <p>Selecione um evento para visualizar as ocorrências</p>
        </div>
      )}

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Evento *</label>
                {modal.data.mode === 'create' ? (
                  <input
                    type="text"
                    value={eventos.find(e => e.id == selectedEvento)?.nome || ''}
                    readOnly
                    className="form-input bg-gray-100"
                  />
                ) : (
                  <select
                    value={form.values.evento_id}
                    onChange={(e) => form.setValue('evento_id', e.target.value)}
                    onBlur={() => form.setTouched('evento_id')}
                    className={`form-select ${form.errors.evento_id && form.touched.evento_id ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione um evento</option>
                    {eventos.map((evento) => (
                      <option key={evento.id} value={evento.id}>
                        {evento.nome}
                      </option>
                    ))}
                  </select>
                )}
                {form.errors.evento_id && form.touched.evento_id && (
                  <div className="form-error">{form.errors.evento_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Data de Abertura *</label>
                <input
                  type="date"
                  value={form.values.data_abertura}
                  onChange={modal.data.mode === 'create' ? undefined : (e) => form.setValue('data_abertura', e.target.value)}
                  onBlur={() => form.setTouched('data_abertura')}
                  readOnly={modal.data.mode === 'create'}
                  className={`form-input ${modal.data.mode === 'create' ? 'bg-gray-100' : ''} ${form.errors.data_abertura && form.touched.data_abertura ? 'border-red-500' : ''}`}
                />
                {form.errors.data_abertura && form.touched.data_abertura && (
                  <div className="form-error">{form.errors.data_abertura}</div>
                )}
              </div>

              {modal.data.mode !== 'create' && (
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    value={form.values.status}
                    onChange={(e) => form.setValue('status', e.target.value)}
                    onBlur={() => form.setTouched('status')}
                    className={`form-select ${form.errors.status && form.touched.status ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione o status</option>
                    <option value="ABERTO">Aberto</option>
                    <option value="EM_ANDAMENTO">Em Andamento</option>
                    <option value="FECHADO">Fechado</option>
                  </select>
                  {form.errors.status && form.touched.status && (
                    <div className="form-error">{form.errors.status}</div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select
                  value={form.values.tipo_id}
                  onChange={(e) => form.setValue('tipo_id', e.target.value)}
                  className="form-select"
                >
                  <option value="">Selecione um tipo</option>
                  {tipos.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Nome do Solicitante</label>
                <input
                  type="text"
                  value={form.values.nome_solicitante}
                  onChange={(e) => form.setValue('nome_solicitante', e.target.value)}
                  className="form-input"
                  placeholder="Digite o nome do solicitante"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  value={form.values.descricao}
                  onChange={(e) => form.setValue('descricao', e.target.value)}
                  className="form-input"
                  placeholder="Descreva a ocorrência"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Observações</label>
                <textarea
                  value={form.values.observacoes}
                  onChange={(e) => form.setValue('observacoes', e.target.value)}
                  className="form-input"
                  placeholder="Observações adicionais"
                  rows="2"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OcorrenciasKanban;